import { useCallback, useEffect, useState } from "react";
import { adminPaymentSettings, adminUpdatePaymentSettings, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";
import AdminFormInput from "../components/AdminFormInput";
import AdminPrimaryButton from "../components/AdminPrimaryButton";
import AdminToggle from "../components/AdminToggle";

export default function PaymentConfigTab() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [rpid, setRpId] = useState("");
  const [rpsec, setRpSec] = useState("");

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const d = await adminPaymentSettings();
      setData(d);
    } catch (e) {
      setLoadErr(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (field, value) => {
    setBusy(true);
    try {
      await adminUpdatePaymentSettings({ [field]: value });
      toast.success("Updated");
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const saveRazorpay = async () => {
    if (!rpid || !rpsec) { toast.error("Enter both key id and secret"); return; }
    setBusy(true);
    try {
      await adminUpdatePaymentSettings({ razorpay_key_id: rpid, razorpay_key_secret: rpsec });
      toast.success("Razorpay keys saved");
      setRpId("");
      setRpSec("");
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  if (loadErr) return <p className="text-red-600 text-sm">{loadErr}</p>;
  if (!data) return <p className="text-slate-400 text-sm">Loading…</p>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="payment-config-tab">
      {/* Stripe */}
      <AdminCard>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Stripe</p>
              <h3 className="text-lg font-semibold text-slate-900 mt-1">
                Stripe is {data.stripe_enabled ? "enabled" : "disabled"}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Key: <span className="font-mono text-slate-700">{data.stripe_key_masked}</span>
              </p>
            </div>
            <AdminToggle
              enabled={data.stripe_enabled}
              onToggle={() => toggle("stripe_enabled", !data.stripe_enabled)}
              disabled={busy || !data.stripe_key_present}
              size={48}
              data-testid="stripe-toggle"
              title={!data.stripe_key_present ? "STRIPE_API_KEY not set in .env" : "Toggle Stripe"}
            />
          </div>
          {!data.stripe_key_present && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 font-mono">
              STRIPE_API_KEY is not configured in backend .env. Add it and restart the backend.
            </div>
          )}
        </div>
      </AdminCard>

      {/* Razorpay */}
      <AdminCard>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Razorpay</p>
              <h3 className="text-lg font-semibold text-slate-900 mt-1">
                Razorpay is {data.razorpay_enabled ? "enabled" : "disabled"}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Key id: <span className="font-mono text-slate-700">{data.razorpay_key_id_masked}</span>
              </p>
            </div>
            <AdminToggle
              enabled={data.razorpay_enabled}
              onToggle={() => toggle("razorpay_enabled", !data.razorpay_enabled)}
              disabled={busy || !data.razorpay_configured}
              size={48}
              data-testid="razorpay-toggle"
              title={!data.razorpay_configured ? "Configure Razorpay keys below first" : "Toggle Razorpay"}
            />
          </div>

          {!data.razorpay_configured && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 mb-4">
              Razorpay is not configured. Paste your Key ID and Key Secret below to enable.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <AdminFormInput
              type="text"
              value={rpid}
              onChange={(e) => setRpId(e.target.value)}
              placeholder="Razorpay key id (rzp_...)"
              data-testid="razorpay-key-id-input"
            />
            <AdminFormInput
              type="password"
              value={rpsec}
              onChange={(e) => setRpSec(e.target.value)}
              placeholder="Razorpay key secret"
              data-testid="razorpay-key-secret-input"
            />
          </div>
          <div className="mt-3">
            <AdminPrimaryButton
              busy={busy}
              onClick={saveRazorpay}
              busyLabel="Saving…"
              data-testid="razorpay-save-button"
            >
              Save Razorpay keys
            </AdminPrimaryButton>
          </div>
        </div>
      </AdminCard>
    </div>
  );
}
